import { PlusOutlined } from '@ant-design/icons';
import {
  App, Button, Divider, Flex,
  Skeleton, Space, Typography,
} from 'antd';
import useFetchedData from 'hooks/useFetchedData';
import useLoadings from 'hooks/useLoadings';
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { captureException } from 'utils/errors';
import http, { getHttpErrorMessage } from 'utils/http';
import useEventEffect from 'hooks/useEvent';
import ContactDrawerForm from '../../../ContactDrawerForm';
import Contact from './Contact';

function Contacts() {
  const { id } = useParams();
  const { message, modal } = App.useApp();
  const [selectedContact, setSelectedContact] = useState();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const { setLoading, isLoading, resetLoadings } = useLoadings();
  const { data: contacts, loading: contactsLoading, mutate: refreshContacts } = useFetchedData(
    {
      url: `v1/organizations/{{organizationId}}/companies/${id}/contacts/`,
      query: {
        page: 1,
        page_size: 300,
      },
    },
    ({ results: contacts } = {}) => contacts,
  );

  useEventEffect(() => {
    refreshContacts();
  }, 'refresh_contacts');

  const onMakePrimary = (contactId) => {
    setLoading('setPrimary', true);
    http.put(`/v1/organizations/{{organizationId}}/companies/${id}/contacts/${contactId}/`, { is_primary: true })
      .then(() => {
        message.success('Contact updated successfully!');
        refreshContacts();
      })
      .catch((e) => {
        message.error(getHttpErrorMessage(e));
        captureException(e);
      })
      .finally(() => {
        setLoading('setPrimary', false);
      });
  };

  const handleDelete = (contact) => {
    modal.confirm({
      title: `Remove ${contact.first_name} ${contact.last_name}`,
      content: 'By confirming, this contact will be permanently removed from the Company&apos;s contact list.',
      okText: 'Remove',
      cancelText: 'Cancel',
      okButtonProps: {
        danger: true,
      },
      onOk: () => {
        setLoading('delete', true);
        return http.delete(`/v1/organizations/{{organizationId}}/companies/${id}/contacts/${contact.id}/`)
          .then(() => {
            message.success('Contact deleted successfully!');
            onClose(true);
          })
          .catch((e) => {
            message.error(getHttpErrorMessage(e));
            captureException(e);
          })
          .finally(() => {
            setLoading('delete', false);
          });
      },
    });
  };

  const onClose = (needRefresh) => {
    if (needRefresh) {
      refreshContacts();
    }

    setDrawerOpen(false);
    setSelectedContact();
    resetLoadings();
  };

  async function onSubmit(data) {
    if (!selectedContact?.id) {
      await http.post(`/v1/organizations/{{organizationId}}/companies/${id}/contacts/`, data);
    } else {
      await http.put(`/v1/organizations/{{organizationId}}/companies/${id}/contacts/${selectedContact.id}/`, data);
    }
    onClose(true);
  }

  const handleCancelWorkflow = (workflowRun) => {
    modal.confirm({
      title: 'Cancel Workflow',
      content: (
        <p>
          Are you sure you want to cancel the workflow &quot;
          <strong>{workflowRun?.workflow?.name}</strong>
          &quot;?
        </p>
      ),
      okText: 'Yes, Cancel',
      cancelText: 'No',
      okType: 'danger',
      onOk: () => http.post(`/v1/organizations/{{organizationId}}/workflow-runs/${workflowRun.id}/cancel/`)
        .then(() => {
          message.success('Workflow cancelled successfully!');
          refreshContacts();
        })
        .catch((e) => {
          message.error(getHttpErrorMessage(e));
          captureException(e);
        })
        .finally(() => {
          setLoading(`cancel_workflow_${workflowRun.id}`, false);
        }),
    });
  };
  return (
    <>
      <Flex vertical className="border border-1 border=[#0000000F] rounded-lg mt-10 px-3 py-2">
        <Flex justify="space-between">
          <Typography.Title level={4} className="mb-0 font-bold">
            Contacts
          </Typography.Title>
          <Button onClick={() => setDrawerOpen(true)} icon={<PlusOutlined />} />
        </Flex>
        {(!contacts?.length || contactsLoading) ? <Skeleton className="mt-2" /> : (
          <Space direction="vertical" className="mt-2" split={<Divider className="my-0" />}>
            {contacts.map((contact) => (
              <Contact
                key={contact.id}
                contact={contact}
                onEdit={() => {
                  setSelectedContact(contact);
                  setDrawerOpen(true);
                }}
                onMakePrimary={onMakePrimary}
                onRemove={() => handleDelete(contact)}
                onCancelWorkflow={handleCancelWorkflow}
              />
            ))}
          </Space>
        )}
      </Flex>

      <ContactDrawerForm
        data={selectedContact}
        open={drawerOpen}
        onSubmit={onSubmit}
        onClose={onClose}
        onDelete={handleDelete}
      />
    </>
  );
}

export default Contacts;
